import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { COMMUNICATION_EVENTS, ROLES, STATUS_IDENTIFIERS } from "models";
import { getKeylockerCommEventLogs, getKeylockerNonEventActivityTitle, getName } from "modules/AppointmentDetails/components/ActivityLog/utils/events";
import { ACTIVITY_LOG_TYPE } from "modules/AppointmentDetails/components/ActivityLog/utils/filters";

import type { APPOINTMENT_LOG_TYPES, Appointment, CommunicationReceiver, KEYLOCKER_PIN_REQUEST_TYPES } from "models";
import type { EVENT_TYPES } from "modules/AppointmentDetails/components/ActivityLog/utils/events";

export type ActivityLogs = {
  statusIdentifier?: STATUS_IDENTIFIERS;
  eventType?: EVENT_TYPES;
  activityType: ACTIVITY_LOG_TYPE;
  time: string;
  note?: string | null;
  name?: string;
  isDeskCheckOut?: boolean;
  keylockerPinRequestType?: KEYLOCKER_PIN_REQUEST_TYPES;
  activityTitle?: string;
  appointmentLogType?: APPOINTMENT_LOG_TYPES;
  receiver?: CommunicationReceiver;
  isDateOnly?: boolean;
};

export const useActivityLogs = (appointment?: Appointment) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const tableData: ActivityLogs[] = [];
    if (!appointment) return tableData;

    if (appointment.logs?.length) {
      appointment.logs.forEach(log => {
        tableData.push({
          time: log.created_on,
          activityType: ACTIVITY_LOG_TYPE.Appointment,
          name: log.user_name,
          note: log.note,
          appointmentLogType: log.appointment_log_type_id
        });
      });
    }

    if (appointment.customer_communication?.events?.length) {
      appointment.customer_communication.events.forEach(event => {
        const receivers = event.receivers ?? [];
        if (!receivers.length) {
          tableData.push({
            time: event.created_on,
            activityType: ACTIVITY_LOG_TYPE.Customer,
            name: getName(event),
            eventType: event.type
          });
          return;
        }

        receivers.forEach(receiver => {
          tableData.push({
            time: event.created_on,
            activityType: ACTIVITY_LOG_TYPE.Customer,
            name: receiver.destination || getName(event),
            eventType: event.type,
            receiver
          });
        });
      });
    }

    if (appointment.desk_communications?.length) {
      appointment.desk_communications.forEach(deskComm => {
        deskComm.events?.forEach(event => {
          tableData.push({
            time: event.created_on,
            activityType: ACTIVITY_LOG_TYPE.Desk,
            name: getName(event),
            eventType: event.type,
            isDeskCheckOut: deskComm.is_checking_out
          });
        });
      });
    }

    if (appointment.keylocker_communications?.length) {
      appointment.keylocker_communications.forEach(keylockerComm => {
        keylockerComm.events?.forEach(event => {
          const data = getKeylockerCommEventLogs(appointment, event, t);

          tableData.push(...data);
        });
      });
    }

    if (appointment.acses_customer_communication?.events?.length) {
      appointment.acses_customer_communication.events.forEach(event => {
        tableData.push({
          time: event.created_on,
          activityType: ACTIVITY_LOG_TYPE.AcsesReceiver,
          name: getName(event),
          eventType: event.type,
          note: t("v8_acses_event_drop_back_pin_sent", "Acses event drop back PIN sent")
        });
      });
    }

    if (appointment.dms_created_at && !appointment.dms_created_at.startsWith("0001-01-01T00:00:00")) {
      tableData.push({
        time: appointment.dms_created_at,
        activityType: ACTIVITY_LOG_TYPE.WorkOrder,
        name: "DMS",
        isDateOnly: true
      });
    }

    if (appointment.car_in_shop) {
      tableData.push({
        time: appointment.car_in_shop,
        activityType: ACTIVITY_LOG_TYPE.Appointment,
        name: appointment.car_in_shop_set_by ? `${appointment.car_in_shop_set_by.first_name || ""} ${appointment.car_in_shop_set_by.last_name || ""}`.trim() : "",
        note: t("v8_car_in_shop", "Car in Shop"),
        statusIdentifier: STATUS_IDENTIFIERS.CarInShop
      });
    }

    if (appointment.car_out_of_shop) {
      tableData.push({
        time: appointment.car_out_of_shop,
        activityType: ACTIVITY_LOG_TYPE.Appointment,
        name: appointment.car_out_of_shop_set_by ? `${appointment.car_out_of_shop_set_by.first_name || ""} ${appointment.car_out_of_shop_set_by.last_name || ""}` : "",
        note: t("v8_car_out_of_shop", "Car out of shop"),
        statusIdentifier: STATUS_IDENTIFIERS.CarOutOfShop
      });
    }

    appointment.status_history?.forEach(status => {
      let name = status.last_user;
      if (status.user_id === 0 && status.identifier === STATUS_IDENTIFIERS.CustomerAnsweredStatus) {
        const eventDiagnoseOverviewAnswered = appointment.customer_communication?.events?.find(event => event.type === COMMUNICATION_EVENTS.DiagnoseOverviewAnswered);

        if (eventDiagnoseOverviewAnswered) name = getName(eventDiagnoseOverviewAnswered);
      }

      tableData.push({
        time: status.created_on,
        activityType: ACTIVITY_LOG_TYPE.Appointment,
        name: status.identifier === STATUS_IDENTIFIERS.CanceledStatus && status.is_dms ? t("v8_canceled_by_dms", "Canceled by DMS") : name,
        statusIdentifier: status.identifier
      });
    });

    appointment.car_check_events?.forEach(ce => {
      tableData.push({
        time: ce.created_on,
        activityType: ACTIVITY_LOG_TYPE.CheckEvent,
        name: ce.user_full_name || "",
        note: ce.checklist_name || "",
        eventType: ce.type
      });
    });

    if (appointment.car_check_paused_at) {
      tableData.push({
        time: appointment.car_check_paused_at,
        activityType: ACTIVITY_LOG_TYPE.Appointment,
        name: "",
        note: t("v8_paused_check", "Paused check"),
        statusIdentifier: STATUS_IDENTIFIERS.CarCheckStartedStatus
      });
    }

    if (appointment.sharebox_key_dropped_at) {
      tableData.push({
        time: appointment.sharebox_key_dropped_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: "",
        activityTitle: getKeylockerNonEventActivityTitle(t, null, true),
        note: t("v8_keylocker_sharebox_key_dropped", "Sharebox key dropped")
      });
    }

    if (appointment.sharebox_key_picked_up_at) {
      tableData.push({
        time: appointment.sharebox_key_picked_up_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: appointment.sharebox_key_picked_up_by
          ? `${appointment.sharebox_key_picked_up_by.first_name} ${appointment.sharebox_key_picked_up_by.last_name}`.trim()
          : "",
        note: t("v8_keylocker_sharebox_key_picked_up", "Sharebox key picked up"),
        activityTitle: getKeylockerNonEventActivityTitle(t, appointment.sharebox_key_picked_up_by?.role_id)
      });
    }

    if (appointment.sharebox_key_dropped_back_at) {
      tableData.push({
        time: appointment.sharebox_key_dropped_back_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: appointment.sharebox_key_dropped_back_by
          ? `${appointment.sharebox_key_dropped_back_by.first_name} ${appointment.sharebox_key_dropped_back_by.last_name}`.trim()
          : "",
        note: t("v8_keylocker_sharebox_key_dropped_back", "Sharebox key dropped back"),
        activityTitle: getKeylockerNonEventActivityTitle(t, appointment.sharebox_key_dropped_back_by?.role_id)
      });
    }

    if (appointment.sharebox_key_picked_up_back_at) {
      tableData.push({
        time: appointment.sharebox_key_picked_up_back_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: appointment.sharebox_key_picked_up_back_by
          ? `${appointment.sharebox_key_picked_up_back_by.first_name} ${appointment.sharebox_key_picked_up_back_by.last_name}`.trim()
          : "",
        note: t("v8_keylocker_sharebox_key_picked_up_back", "Sharebox key picked up back"),
        activityTitle: getKeylockerNonEventActivityTitle(t, appointment.sharebox_key_picked_up_back_by?.role_id, !appointment.sharebox_key_picked_up_back_by)
      });
    }

    if (appointment.acses_key_dropped_at) {
      tableData.push({
        time: appointment.acses_key_dropped_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: "",
        activityTitle: getKeylockerNonEventActivityTitle(t, null, true),
        note: t("v8_keylocker_acses_key_dropped", "Acses key dropped")
      });
    }

    if (appointment.acses_key_picked_up_at) {
      tableData.push({
        time: appointment.acses_key_picked_up_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: appointment.acses_key_pick_up_by ? `${appointment.acses_key_pick_up_by.first_name} ${appointment.acses_key_pick_up_by.last_name}`.trim() : "",
        note: t("v8_keylocker_acses_key_picked_up", "Acses key picked up"),
        activityTitle: getKeylockerNonEventActivityTitle(t, appointment.acses_key_pick_up_by?.role_id || ROLES.Mechanic)
      });
    }

    if (appointment.acses_key_dropped_back_at) {
      tableData.push({
        time: appointment.acses_key_dropped_back_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: appointment.acses_key_drop_back_by ? `${appointment.acses_key_drop_back_by.first_name} ${appointment.acses_key_drop_back_by.last_name}`.trim() : "",
        note: t("v8_keylocker_acses_key_dropped_back", "Acses key dropped back"),
        activityTitle: getKeylockerNonEventActivityTitle(t, appointment.acses_key_drop_back_by?.role_id, !appointment.acses_key_drop_back_by?.role_id)
      });
    }

    if (appointment.acses_key_picked_up_back_at)
      tableData.push({
        time: appointment.acses_key_picked_up_back_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        name: appointment.acses_key_assist_pick_up_by
          ? `${appointment.acses_key_assist_pick_up_by.first_name} ${appointment.acses_key_assist_pick_up_by.last_name}`.trim()
          : "",
        note: t("v8_keylocker_acses_key_picked_up_back", "Acses key picked up back"),
        activityTitle: getKeylockerNonEventActivityTitle(t, appointment.acses_key_assist_pick_up_by?.role_id, !appointment.acses_key_assist_pick_up_by?.role_id)
      });

    return tableData.sort((t1, t2) => t2.time.localeCompare(t1.time));
  }, [
    appointment?.logs,
    appointment?.dms_created_at,
    appointment?.customer_communication,
    appointment?.desk_communications,
    appointment?.keylocker_communications,
    appointment?.acses_customer_communication,
    appointment?.car_in_shop,
    appointment?.car_out_of_shop,
    appointment?.status_history,
    appointment?.car_check_events,
    appointment?.car_check_paused_at,
    appointment?.sharebox_key_dropped_at,
    appointment?.sharebox_key_picked_up_at,
    appointment?.sharebox_key_dropped_back_at,
    appointment?.sharebox_key_picked_up_back_at,
    appointment?.acses_key_dropped_at,
    appointment?.acses_key_picked_up_at,
    appointment?.acses_key_dropped_back_at,
    appointment?.acses_key_picked_up_back_at,
    t
  ]);
};

export const useActivityLogsCount = (appointment?: Appointment) => useActivityLogs(appointment).length;
